import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { DealAccountLinkInput } from "@colab/shared";

// PUT /api/admin/deals/:hubspotDealId/account
//
// Replaces /app/admin/deals/_actions.ts::linkDealAccount. Pessimistic — the
// first link triggers a HubSpot Company read + an `accounts` row insert on
// the worker (services/accounts.ts:130–186), which may take a moment.
// Returning the new `accountId` is the whole point of the call, so
// synthesizing one client-side would be lossy.
//
// Invalidates the deal detail + queue rows so the drawer + list both pick
// up the new account snapshot on next render.

type Vars = { hubspotDealId: string } & DealAccountLinkInput;

export function useLinkDealAccount() {
  const queryClient = useQueryClient();
  return useMutation<{ accountId: string }, ApiClientError, Vars>({
    mutationFn: ({ hubspotDealId, hubspotCompanyId }) =>
      apiFetch<{ accountId: string }>(
        `/admin/deals/${hubspotDealId}/account`,
        {
          method: "PUT",
          body: JSON.stringify({ hubspotCompanyId }),
        },
      ),
    onSuccess: (_data, { hubspotDealId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.deals.detail(hubspotDealId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.deals.list() });
      // Phase 7 sub-bucket 16 — admin drawer cache. Kanban is unaffected
      // (account snapshot doesn't surface on the cards), so we don't
      // invalidate `admin.deals.kanban()`.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.deals.detail(hubspotDealId),
      });
      toast.success("Account linked");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to link account");
    },
  });
}
